import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة ثابت حاصل الإذابة (Ksp) - حساب الذوبانية والتركيز المولاري";
const DESCRIPTION =
  "حاسبة ثابت حاصل الإذابة (Ksp) المجانية - احسب قيمة Ksp أو الذوبانية المولارية للمركبات الأيونية بدقة. أداة تعليمية للكيمياء تدعم أنواع مختلفة من الأملاح مع شرح للمعادلات والأمثلة.";
const KEYWORDS =
  "حاسبة Ksp, ثابت حاصل الإذابة, حساب الذوبانية المولارية, كيمياء تحليلية, توازن أيوني, solubility product constant calculator, molar solubility";
const OG_URL = "https://do-calculate.com/calculator/ar/ksp-calculator/";

// JSON-LD Schema for SEO
const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة ثابت حاصل الإذابة (Ksp)",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "EducationApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات العلمية",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة Ksp",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "ما هو ثابت حاصل الإذابة (Ksp)؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "ثابت حاصل الإذابة (Ksp) هو ثابت التوازن لمركب أيوني شحيح الذوبان في الماء، ويمثل حاصل ضرب تراكيز الأيونات المكونة للملح كل منها مرفوع لأس يساوي معاملها في معادلة التوازن.",
        },
      },
      {
        "@type": "Question",
        name: "كيف يتم حساب Ksp من الذوبانية؟",
        acceptedAnswer: {
          "@type": "Answer",
          text: "يعتمد ذلك على نوع الملح. لملح AB، يكون Ksp = s². لملح AB2، يكون Ksp = 4s³. لملح A2B3، يكون Ksp = 108s⁵، حيث s هي الذوبانية المولارية.",
        },
      },
    ],
  },
];

const SALT_TYPES = {
  AB: { label: "ملح ثنائي أيوني (AB) مثل AgCl", x: 1, y: 1 },
  AB2: { label: "ملح ثلاثي أيوني (AB2) مثل Mg(OH)2", x: 1, y: 2 },
  A2B: { label: "ملح ثلاثي أيوني (A2B) مثل Ag2CrO4", x: 2, y: 1 },
  AB3: { label: "ملح رباعي أيوني (AB3) مثل Al(OH)3", x: 1, y: 3 },
  A3B: { label: "ملح رباعي أيوني (A3B) مثل Li3PO4", x: 3, y: 1 },
  A2B3: { label: "ملح خماسي أيوني (A2B3) مثل As2S3", x: 2, y: 3 },
  A3B2: { label: "ملح خماسي أيوني (A3B2) مثل Ca3(PO4)2", x: 3, y: 2 },
};

function KspCalculator() {
  // States
  const [calcMode, setCalcMode] = useState("s_to_ksp"); // s_to_ksp or ksp_to_s
  const [saltType, setSaltType] = useState("AB");
  const [molarSolubility, setMolarSolubility] = useState("");
  const [kspValue, setKspValue] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateKsp = () => {
    try {
      setError("");
      const type = SALT_TYPES[saltType];
      const x = type.x;
      const y = type.y;

      if (calcMode === "s_to_ksp") {
        const s = parseFloat(molarSolubility);
        if (isNaN(s) || s <= 0) {
          setError("يرجى إدخال قيمة صحيحة للذوبانية المولارية (أكبر من الصفر)");
          setResult(null);
          return;
        }

        // Ksp = (x^x) * (y^y) * s^(x+y)
        const ksp = Math.pow(x, x) * Math.pow(y, y) * Math.pow(s, x + y);
        setResult({
          ksp: ksp,
          s: s,
          formula: `Ksp = [A]${x > 1 ? `^${x}` : ""} [B]${y > 1 ? `^${y}` : ""}`,
          mathExpr: `(${x}^${x}) * (${y}^${y}) * s^${x + y}`,
        });
      } else {
        const ksp = parseFloat(kspValue);
        if (isNaN(ksp) || ksp <= 0) {
          setError("يرجى إدخال قيمة صحيحة لـ Ksp (أكبر من الصفر)");
          setResult(null);
          return;
        }

        // s = (Ksp / (x^x * y^y)) ^ (1 / (x+y))
        const divisor = Math.pow(x, x) * Math.pow(y, y);
        const s = Math.pow(ksp / divisor, 1 / (x + y));
        setResult({
          ksp: ksp,
          s: s,
          formula: `s = (Ksp / (${x}^${x} * ${y}^${y}))^(1/${x + y})`,
          mathExpr: `s = [Ksp / ${divisor}]^(1/${x + y})`,
        });
      }
    } catch (err) {
      setError("حدث خطأ في الحساب. يرجى مراجعة المدخلات.");
      setResult(null);
    }
  };

  useEffect(() => {
    if (calcMode === "s_to_ksp" && molarSolubility) {
      calculateKsp();
    } else if (calcMode === "ksp_to_s" && kspValue) {
      calculateKsp();
    }
  }, [calcMode, saltType, molarSolubility, kspValue]);

  const handleClear = () => {
    setMolarSolubility("");
    setKspValue("");
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة ثابت حاصل الإذابة (Ksp)</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                تساعدك هذه الحاسبة على <strong>حساب ثابت حاصل الإذابة (Ksp)</strong> من الذوبانية المولارية، أو العكس. ثابت حاصل الإذابة هو مقياس لمدى ذوبان المركبات الأيونية شحيحة الذوبان في المحاليل المائية، وهو ضروري لفهم عمليات الترسيب والتوازن الكيميائي.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>إعدادات الحساب</h2>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label >ماذا تريد أن تحسب؟</label>
                  <select
                    className="form-select"
                    value={calcMode}
                    onChange={(e) => {
                      setCalcMode(e.target.value);
                      setResult(null);
                    }}
                  >
                    <option value="s_to_ksp">حساب Ksp من الذوبانية (s)</option>
                    <option value="ksp_to_s">حساب الذوبانية (s) من Ksp</option>
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label >نوع الملح / الصيغة</label>
                  <select
                    className="form-select"
                    value={saltType}
                    onChange={(e) => setSaltType(e.target.value)}
                  >
                    {Object.entries(SALT_TYPES).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mt-3">
                {calcMode === "s_to_ksp" ? (
                  <div className="col-sm-12">
                    <label >الذوبانية المولارية (s) [mol/L]</label>
                    <input
                      type="number"
                      className="form-control"
                      value={molarSolubility}
                      onChange={(e) => setMolarSolubility(e.target.value)}
                      placeholder="مثال: 0.0013"
                    />
                  </div>
                ) : (
                  <div className="col-sm-12">
                    <label >قيمة ثابت حاصل الإذابة (Ksp)</label>
                    <input
                      type="number"
                      className="form-control"
                      value={kspValue}
                      onChange={(e) => setKspValue(e.target.value)}
                      placeholder="مثال: 1.8e-10"
                    />
                  </div>
                )}
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  امسح البيانات
                </button>
              </div>

              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>تنبيه:</strong> {error}
                </div>
              )}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>النتائج:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>المتغير</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>القيمة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>ثابت حاصل الإذابة (Ksp)</td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: "#1a5f3f" }}>
                            {result.ksp.toExponential(4)}
                          </td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>الذوبانية المولارية (s)</td>
                          <td style={{ textAlign: "center", fontWeight: "bold", color: "#0f3d28" }}>
                            {result.s.toExponential(4)} mol/L
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header"><strong>منطق الحساب</strong></div>
                      <div className="info-section-body">
                        <p><strong>المعادلة المستخدمة:</strong> {result.formula}</p>
                        <p><strong>العلاقة الرياضية:</strong> {result.mathExpr}</p>
                        <div className="alert alert-info mt-2" style={{ fontSize: "14px" }}>
                          تم حساب القيمة بناءً على معامل الأيونات (Stoichiometry) للملح المختار.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      {/* Informational Content */}
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><strong>ما هو ثابت حاصل الإذابة (Ksp)؟</strong></div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  ثابت حاصل الإذابة (Ksp) هو نوع من ثوابت التوازن التي تنطبق على الأملاح الأيونية شحيحة الذوبان. عندما يتم وضع ملح صلب في الماء، ينشأ توازن بين الملح الصلب والأيونات المذابة في المحلول المشبع.
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li><strong>المحلول المشبع:</strong> هو المحلول الذي يحتوي على أقصى كمية ممكنة من المذاب عند درجة حرارة معينة.</li>
                  <li><strong>الأهمية:</strong> يستخدم Ksp للتنبؤ بما إذا كان الراسب سيتكون عند خلط محلولين أيونيين.</li>
                  <li><strong>العلاقة مع الذوبانية:</strong> كلما زادت قيمة Ksp، زادت ذوبانية الملح في الماء.</li>
                </ul>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>جدول علاقات Ksp والذوبانية (s)</strong></div>
              <div className="info-section-body">
                <table className="table bmi-table">
                  <thead>
                    <tr>
                      <th >نوع الملح</th>
                      <th >مثال</th>
                      <th >علاقة Ksp بـ (s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>AB</td><td>AgCl</td><td>Ksp = s²</td></tr>
                    <tr><td>AB2 أو A2B</td><td>PbCl2, Ag2CrO4</td><td>Ksp = 4s³</td></tr>
                    <tr><td>AB3 أو A3B</td><td>Al(OH)3</td><td>Ksp = 27s⁴</td></tr>
                    <tr><td>A2B3</td><td>As2S3</td><td>Ksp = 108s⁵</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>أمثلة عملية</strong></div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px" }}>مثال 1: حساب Ksp لملح AgCl</h3>
                <p>إذا كانت ذوبانية كلوريد الفضة (AgCl) هي 1.3 × 10⁻⁵ mol/L:</p>
                <p>بما أنه ملح من نوع AB، فإن Ksp = s² = (1.3 × 10⁻⁵)² = 1.69 × 10⁻¹⁰.</p>

                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>مثال 2: حساب الذوبانية من Ksp</h3>
                <p>إذا كان Ksp لهيدروكسيد المغنيسيوم Mg(OH)2 هو 1.8 × 10⁻¹¹:</p>
                <p>بما أنه ملح من نوع AB2، فإن Ksp = 4s³، وبالحل لـ s نجد أن s = 1.65 × 10⁻⁴ mol/L.</p>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header"><strong>الأسئلة الشائعة (FAQ)</strong></div>
              <div className="info-section-body">
                <div className="mb-3">
                  <strong>1. هل تتغير قيمة Ksp بتغير درجة الحرارة؟</strong>
                  <p>نعم، ثابت حاصل الإذابة هو ثابت توازن، وقيمته تعتمد كلياً على درجة الحرارة. عادة ما تزداد الذوبانية وKsp مع زيادة درجة الحرارة لمعظم الأملاح.</p>
                </div>
                <div className="mb-3">
                  <strong>2. ما الفرق بين الذوبانية وثابت حاصل الإذابة؟</strong>
                  <p>الذوبانية هي كمية المادة التي تذوب لتكوين محلول مشبع (تقاس بـ g/L أو mol/L)، بينما Ksp هو حاصل ضرب تراكيز الأيونات عند التوازن.</p>
                </div>
                <div className="mb-3">
                  <strong>3. ماذا يعني "تأثير الأيون المشترك"؟</strong>
                  <p>إضافة أيون مشترك للمحلول يقلل من ذوبانية الملح شحيح الذوبان حسب مبدأ لوشاتيليه، مما يؤدي إلى ترسيب جزء من الملح المذاب.</p>
                </div>
                <div className="mb-3">
                  <strong>4. كيف أعرف إذا كان الملح سيترسب؟</strong>
                  <p>يتم حساب حاصل ضرب التراكيز الفعلي (Qsp). إذا كان Qsp {">"} Ksp، سيتكون راسب حتى يصل المحلول لحالة التشبع.</p>
                </div>
                <div className="mb-3">
                  <strong>5. هل يمكن مقارنة ذوبانية ملحين من خلال Ksp فقط؟</strong>
                  <p>يمكن ذلك فقط إذا كان الملحان من نفس النوع (مثلاً كلاهما AB). إذا اختلف نوع الملح، يجب حساب الذوبانية المولارية للمقارنة الدقيقة.</p>
                </div>
                <div className="mb-3">
                  <strong>6. كيف أحول من g/L إلى mol/L لاستخدام الحاسبة؟</strong>
                  <p>قم بقسمة الذوبانية بالجرام/لتر على الكتلة المولية للملح (Molar Mass) للحصول على الذوبانية المولارية (s).</p>
                </div>
                <div className="mb-3">
                  <strong>7. لماذا لا يظهر الملح الصلب في معادلة Ksp؟</strong>
                  <p>لأن تركيز المواد الصلبة النقية يعتبر ثابتاً ولا يدرج في تعبير ثابت التوازن الكيميائي.</p>
                </div>
                <div className="mb-3">
                  <strong>8. ما هي وحدة قياس Ksp؟</strong>
                  <p>ثوابت التوازن في الكيمياء تعتبر قيمًا بلا وحدات (Dimensionless) اصطلاحيًا، رغم أن تراكيز الأيونات المستخدمة تكون بالمولارية.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KspCalculator;